import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import API from "../api/axios";
import { startPolling } from "../api/polling";
import { getSocket } from "../socket";

const tabs = [
  {
    to: "/browse",
    label: "Home",
    icon: (active) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill={active ? "currentColor" : "none"} stroke="currentColor" strokeWidth={active ? 0 : 1.8} strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
  },
  {
    to: "/sellers",
    label: "Sellers",
    icon: (active) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" fill={active ? "currentColor" : "none"} opacity={active ? 0.15 : 0} />
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    ),
  },
  {
    to: "/post",
    label: "Sell",
    isSell: true,
    icon: () => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
    ),
  },

  {
    to: "/messages",
    label: "Messages",
    icon: (active) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill={active ? "currentColor" : "none"} stroke="currentColor" strokeWidth={active ? 0 : 1.8} strokeLinecap="round" strokeLinejoin="round" style={{ transform: "scaleX(-1)" }}>
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ),
  },
  {
    to: "/orders",
    label: "Orders",
    icon: (active) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill={active ? "currentColor" : "none"} stroke="currentColor" strokeWidth={active ? 0 : 1.8} strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
        <line x1="3" y1="6" x2="21" y2="6" />
        <path d="M16 10a4 4 0 0 1-8 0" />
      </svg>
    ),
  },
];

export default function BottomNav() {
  const location = useLocation();
  const isLoggedIn = !!localStorage.getItem("token");
  const [msgUnread, setMsgUnread] = useState(0);
  const [hidden, setHidden] = useState(
    () => location.pathname === "/messages" || location.pathname === "/settings",
  );

  const isMessages = location.pathname === "/messages";
  const isSettings = location.pathname === "/settings";

  const [prevPath, setPrevPath] = useState(location.pathname);
  if (prevPath !== location.pathname) {
    setPrevPath(location.pathname);
    setHidden(isMessages || isSettings);
  }

  useEffect(() => {
    if (!isMessages && !isSettings) return;
    const eventName = isMessages ? "messages-toggle-bottomnav" : "settings-toggle-bottomnav";
    const handler = (e) => setHidden(!e.detail?.open);
    window.addEventListener(eventName, handler);
    return () => window.removeEventListener(eventName, handler);
  }, [isMessages, isSettings]);

  useEffect(() => {
    if (!isLoggedIn) return;
    const fetchCount = async () => {
      try {
        const res = await API.get("/messages/unread-count");
        setMsgUnread(res.data?.count || 0);
        return true;
      } catch {
        return false;
      }
    };
    return startPolling(fetchCount, { baseMs: 30000 });
  }, [isLoggedIn, location.pathname]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const onNewMsg = () => setMsgUnread((c) => c + 1);
    socket.on("new-message", onNewMsg);
    return () => socket.off("new-message", onNewMsg);
  }, []);

  const pathMatches = (tabTo) => {
    if (tabTo === "/browse") return location.pathname === "/browse" || location.pathname === "/home";
    return location.pathname.startsWith(tabTo);
  };

  return (
    <nav className={`fixed bottom-0 left-0 right-0 z-50 md:hidden bg-bg/80 backdrop-blur-xl border-t border-border/30 safe-area-pb transition-transform duration-300 ${hidden ? "translate-y-full" : ""}`}>
      <div className="flex items-center justify-around h-14 px-1">
        {tabs.map((tab) => {
          const active = pathMatches(tab.to);

          if (tab.isSell) {
            return (
              <Link
                key={tab.to}
                to={tab.to}
                className="flex flex-col items-center justify-center"
              >
                <span className="w-11 h-11 flex items-center justify-center rounded-full bg-accent text-white shadow-lg shadow-accent/25 transition-transform active:scale-95">
                  {tab.icon(false)}
                </span>
              </Link>
            );
          }

          return (
            <Link
              key={tab.to}
              to={tab.to}
              className={`flex flex-col items-center justify-center gap-1 py-1 px-3 transition-colors ${
                active ? "text-accent" : "text-text-muted"
              }`}
            >
              <span className="relative">
                {tab.icon(active)}
                {tab.label === "Messages" && msgUnread > 0 && (
                  <span className="absolute -top-1 -right-1.5 w-2 h-2 rounded-full bg-accent" />
                )}
              </span>
                <span className={`text-[13px] leading-none mt-px ${active ? "text-accent" : ""}`} style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 550, WebkitTextStroke: "0.3px currentColor" }}>
                {tab.label}
              </span>
              {active && <span className="w-4 h-0.5 rounded-full bg-accent mt-0.5" />}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
